import { InvestmentVehicleCardStyles } from './InvestmentVehicleCard.styles.tsx';
import { Flex, Text, useMantineTheme } from '@mantine/core';
import { ManageRequirementsButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/ManageRequirementsButton.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { InvestmentDetailsPiv, InvestmentDetailsPivAddress } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestmentDetailsRowItem } from '../InvestmentDetailsRowItem/InvestmentDetailsRowItem.tsx';
import { InvestmentPlaceholder } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentPlaceholder/InvestmentPlaceholder.tsx';
import { ManagePivDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/ManagePivDrawer/ManagePivDrawer.tsx';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';
import { useInvestmentDetailsBankInformation } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsBankInformation.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { getLegalEntities } from '@dealstack/frontend/app/services/Person.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
	piv: InvestmentDetailsPiv | null;
	canMutate: boolean;
}

export const InvestmentVehicleCard = ({ piv, canMutate }: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();
	const { dealId } = useRequiredParams('dealId');
	const { data: summary, refetch: refetchSummary } = useInvestmentDetailsSummary();
	const { data: bankInformation, refetch: refetchBankInformation } =
		useInvestmentDetailsBankInformation();
	const [drawerOpen, setDrawerOpen] = useState(false);

	const investorPersonId = summary?.investor.personId ?? null;
	const hasSelectedBankAccount = !!bankInformation?.selectedBankAccountId;

	const { data: pivEntities = [] } = useQuery({
		queryKey: [
			API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET,
			{ dealId, pivOfPersonId: investorPersonId },
		],
		queryFn: () =>
			getLegalEntities(API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, {
				dealId,
				pivOfPersonId: investorPersonId ?? undefined,
			}),
		enabled: drawerOpen && !!investorPersonId,
	});

	const getPivAddress = (address: InvestmentDetailsPivAddress | null): string | null => {
		if (!address) {
			return null;
		}

		const { street, houseNumber, city, postCode, country } = address;
		return [street, houseNumber, city, postCode, country]
			.filter((part) => part && part.trim())
			.join(', ');
	};

	return (
		<>
			<InvestmentDetailsCard
				cardLabel={tMipManage('investment_vehicle_card.piv.card_label')}
				actions={
					<ManageRequirementsButton
						canMutate={canMutate}
						onClick={() => setDrawerOpen(true)}
					>
						{tMipManage('investment_vehicle_card.piv.manage')}
					</ManageRequirementsButton>
				}
			>
				<InvestmentVehicleCardStyles>
					{piv ? (
						<>
							<Flex direction="column" gap="0.25rem" p="0 0.5rem">
								<Text fw={600} c="dark.8">
									{piv.displayName}
								</Text>

								{piv.registrationNumber && (
									<Text fz="sm" fw={400} c="dark.0">
										{tMipManage('investment_vehicle_card.piv.registration', {
											registrationNumber: piv.registrationNumber,
										})}
									</Text>
								)}

								{piv.jurisdiction && (
									<Text fz="sm" fw={400} c="dark.0">
										{tMipManage('investment_vehicle_card.piv.jurisdiction', {
											jurisdiction: piv.jurisdiction,
										})}
									</Text>
								)}
							</Flex>

							{piv.address && (
								<Flex
									gap="0.25rem"
									align="center"
									mt="0.25rem"
									p="0 0.5rem"
									mb="0.5rem"
								>
									<Icon
										variant="map-pin"
										size="lg"
										color={theme.colors.gray[9]}
									/>

									<Text fw={400} c="dark.0">
										{getPivAddress(piv.address)}
									</Text>
								</Flex>
							)}

							{piv.legalRepresentative && (
								<InvestmentDetailsRowItem
									label={
										<Text fw={400} c="dark.0">
											{tMipManage(
												'investment_vehicle_card.piv.legal_representative'
											)}
										</Text>
									}
									content={
										<Text fw={600} c="dark.8">
											{piv.legalRepresentative.displayName}
										</Text>
									}
								/>
							)}
						</>
					) : (
						<InvestmentPlaceholder
							label={tMipManage('investment_vehicle_card.piv.empty')}
							icon="building-office-company-entity-line"
							mih={136}
						/>
					)}
				</InvestmentVehicleCardStyles>
			</InvestmentDetailsCard>

			<ManagePivDrawer
				opened={drawerOpen}
				personId={summary?.investor?.personId || null}
				piv={piv}
				entities={pivEntities}
				hasSelectedBankAccount={hasSelectedBankAccount}
				onClose={() => {
					setDrawerOpen(false);
				}}
				onSaved={() => {
					void refetchSummary();
					void refetchBankInformation();
				}}
			/>
		</>
	);
};
